import { isEmpty } from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import { DAY_IN_MS, localStorage } from '@fiverr-private/futile';
import { clientAllocate, EXPERIMENT_TYPE } from '@fiverr-private/experiments_package';
import { CHAM_PANTHERS_POST_FMP_MORE_GIGS_V4 } from '../../utils/experiments/experimentsEnum';

const STORAGE_KEY_PREFIX = 'FMP_MORE_GIGS';
const DATE = 'date';
const COUNT = 'count';
const BLOCKED_LIST_COUNTRIES = ['India', 'Pakistan', 'Bangladesh', 'Nigeria', 'Indonesia', 'Sri Lanka', 'Kenya', 'Morocco', 'Philippines'];

/**
 * get storage key for offer more gigs feature seen count
 * key is structured by sub_category and userID
 *
 * @param { number } subCategoryId
 * @return { string }
 */
const getStorageKey = ({ subCategoryId }) => {
    const { userId } = getContext();

    return `${STORAGE_KEY_PREFIX}_${subCategoryId}_${userId}`;
};

/**
 * give datetime, checks if it in the last day (24 hrs)
 *
 * @param {Date} date
 * @return {boolean}
 */
const isInTheLastDay = (date) => Date.now() - date <= DAY_IN_MS;

/**
 * We want to know if the offer more gigs step is shown lately.
 * considered shown lately when: seen twice or more in the last day per sub-category
 *
 * @param subCategoryId
 * @returns {boolean}
 */
const hasShownLately = ({ subCategoryId }) => {
    const storageKey = getStorageKey({ subCategoryId });
    const storageMoreGigs = localStorage.get(storageKey);

    // if no storage value then for sure it is not seen lately
    if (!storageMoreGigs) {
        return false;
    }

    // last show is more than a day ago
    if (!isInTheLastDay(storageMoreGigs[DATE])) {
        return false;
    }

    // return true if shown more than 2 times in the last day
    return storageMoreGigs[COUNT] >= 2;
};

/**
 * gets the user country, returns true if its one of the allowed countries of the v4 experiment
 * @param country
 * @returns {boolean}
 */
const isEligibleCountry = (country) => !BLOCKED_LIST_COUNTRIES.includes(country);

/**
 * show more gigs step when:
 * - original message sent without attachments
 * - the step is not shown lately
 *
 * @param {Object} config
 *     @param {Object[]} config.attachments
 *     @param {number} config.subCategoryId
 *     @param {boolean} config.isNight
 *     @param {boolean} config.isOnline
 *     @param {boolean} config.isRnc
 * @returns {boolean}
 */
export const shouldShowMoreGigs = ({ attachments, subCategoryId, isNight, isOnline, isRnc }) =>
    isRnc && !isOnline && isNight && isEmpty(attachments) && !hasShownLately({ subCategoryId });

/**
 * when to show more gigs v4 experiment
 *
 * @param {Object} config
 *     @param {Object[]} config.attachments
 *     @param {number} config.subCategoryId
 *     @param {boolean} config.isNight
 *     @param {boolean} config.isOnline
 *     @param {boolean} config.isRnc
 * @returns {boolean}
 */
export const shouldShowMoreGigsV4 = ({ attachments, isRnc, isOnline, country }) =>
    !isRnc && !isOnline && isEmpty(attachments) && isEligibleCountry(country);

/**
 * we call this method when buyer see the offer more gigs step.
 * we want to update the storage every time he sees the step in order to know if to show it next time.
 *
 * - if last time buyer seen this step was more than 1 day ago (or never),
 *   we reset the storage with seen count 1 and date is now
 * - otherwise we update the seen count
 *
 * @param { number } subCategoryId
 */
export const updateShownStorage = ({ subCategoryId }) => {
    const storageKey = getStorageKey({ subCategoryId });
    let storageMoreGigs = localStorage.get(storageKey);

    // reset if never seen or seen more than a day ago
    if (!storageMoreGigs || !isInTheLastDay(storageMoreGigs[DATE])) {
        storageMoreGigs = {
            [DATE]: Date.now(),
            [COUNT]: 0
        };
    }

    storageMoreGigs[COUNT]++;

    localStorage.set(storageKey, storageMoreGigs);
};

/**
 * Allocate user to the CHAM_PANTHERS_POST_FMP_MORE_GIGS_V4 experiment,
 * and returns a boolean indicating whether the user is in the test group
 *
 * @returns boolean
 * @param inExperimentRollout
 */
export const allocateToMoreGigsV4Experiment = (inExperimentRollout) => {

    const group = clientAllocate({
        experimentId: CHAM_PANTHERS_POST_FMP_MORE_GIGS_V4,
        experimentType: EXPERIMENT_TYPE.USER,
        numOfGroups: 2,
        rolloutResult: inExperimentRollout
    });

    return group === 2;
};
